import { g as generateUUID } from './misc-9fdbcfbb.js';
import { a as arrayToClassString, g as getDeviceType } from './ui-d9d772ae.js';
import { Z as ZIndexManager } from './zIndexManager-3a694c18.js';
import { D as DeviceType } from './ui.type-12100305.js';
import { t as tinycolor } from './tinycolor-ca77a535.js';

/**
 * Vega loader controller to control the loading indicator
 */
class VegaLoaderController {
  /**
   * Create a loading indicator and append it to the container
   *
   * @param {VegaLoaderIndicatorOptions} options - VegaLoaderOptions = {} options of the method
   * @returns {string} The uuid of the loader.
   */
  load(options = {}) {
    const containerSelector = options.containerSelector || 'body';
    const loadingIndicator = this.createLoadingIndicator(options.indicatorOptions);
    const loader = this.createLoader(containerSelector);
    loader.append(loadingIndicator);
    this.appendLoader(loader, containerSelector);
    return loader.id;
  }
  /**
   * Close the loader by id
   *
   * @param {string} id - The id of the loader.
   */
  close(id) {
    const loader = document.getElementById(id);
    if (loader) {
      document
        .querySelector(loader.dataset['container'])
        .classList.remove('vega-loader-container-relative');
      loader.remove();
    }
  }
  /**
   * Create a loader for loading indicator
   *
   * @param {string} containerSelector The container which you want to append
   * @returns {HTMLElement} a wrapper for loader
   */
  createLoader(containerSelector) {
    const loader = document.createElement('div');
    loader.className = arrayToClassString([
      'vega-loader-wrapper',
      containerSelector === 'body' ? 'v-w-screen v-h-screen v-fixed' : 'v-absolute',
    ]);
    loader.dataset['container'] = containerSelector;
    loader.id = `vega-loader-${generateUUID()}`;
    return loader;
  }
  /**
   * Create a loading indicator
   *
   * @param {VegaLoaderIndicatorOptions} indicatorOptions the props of vega loading indicator
   * @returns {HTMLElement} vega-loading-indicator component template
   */
  createLoadingIndicator(indicatorOptions = {}) {
    const loadingIndicator = document.createElement('vega-loading-indicator');
    Object.assign(loadingIndicator, indicatorOptions);
    return loadingIndicator;
  }
  /**
   * Append the loader to the container
   *
   * @param {HTMLElement} loader A loader with loading indicator
   * @param {string} containerSelector The container which you want to append the loader to
   */
  appendLoader(loader, containerSelector) {
    const containerElement = document.querySelector(containerSelector);
    if (containerElement) {
      containerElement.classList.add('vega-loader-container-relative');
      containerElement.append(loader);
    }
  }
}
const VegaLoader = new VegaLoaderController();

/**
 * The page notification controller
 */
class VegaNotifyController {
  /**
   * It creates a new notification element, adds it to the container, and returns the notification's ID
   *
   * @param {VegaNotifyOption} options - VegaNotifyProps
   * @returns {string} The ID of the notification.
   */
  async open(options) {
    const pageNotification = this.createPageNotification(options);
    this.getContainer().append(pageNotification);
    return pageNotification.id;
  }
  /**
   * It closes all notifications
   */
  async closeAll() {
    this.container &&
      Array.from(this.container.children).forEach((item) => {
        void item.close();
      });
  }
  /**
   * It closes the notification with the given id
   *
   * @param {string} id - The id of the notification to close.
   */
  async close(id) {
    await document.getElementById(id).close();
  }
  /**
   * It creates a new page notification element, sets its properties, and returns it
   *
   * @param {VegaNotifyOption} props - VegaNotifyProps - the properties of the notification
   * @returns {HTMLElement} A function that takes in a props object and returns a pageNotification element.
   */
  createPageNotification(props) {
    const pageNotification = document.createElement('vega-page-notification');
    Object.keys(props).forEach((propName) => {
      props[propName] !== undefined && (pageNotification[propName] = props[propName]);
    });
    pageNotification.style.marginBottom = getDeviceType() === DeviceType.MOBILE ? '12px' : '16px';
    pageNotification.id = `vega-page-notification-${generateUUID()}`;
    return pageNotification;
  }
  /**
   * It creates a div element, sets its z-index to be on top of everything else, adds a class to it, and
   * then adds it to the body of the page
   *
   * @returns {HTMLElement} The container is being returned.
   */
  getContainer() {
    if (this.container) {
      return this.container;
    }
    this.container = document.createElement('div');
    ZIndexManager.setZIndex(this.container, 'top');
    this.container.classList.add('vega-page-notification-list');
    if (getDeviceType() === DeviceType.MOBILE) {
      this.container.classList.add('vega-page-notification-list-bottom');
    }
    else {
      this.container.classList.add('vega-page-notification-list-topRight');
    }
    document.body.append(this.container);
    const resizeObserver = new ResizeObserver((entries) => {
      var _a;
      const currentVisible = entries[0].contentRect.height > 0;
      if (!currentVisible) {
        ZIndexManager.removeZIndex(this.container, 'top');
        (_a = this.container) === null || _a === void 0 ? void 0 : _a.remove();
        this.container = null;
      }
    });
    resizeObserver.observe(this.container);
    return this.container;
  }
}
const VegaNotify = new VegaNotifyController();

const formatValue$2 = (tinyColor) => {
  if (!tinyColor) {
    return '';
  }
  const { r, g, b, a } = tinyColor.toRgb();
  return `${r} ${g} ${b} / ${a}`;
};
const OverridableColorTokens = [
  {
    name: 'bg-selected',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(10);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-10);
      }
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.25);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-selected',
  },
  {
    name: 'bg-action',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-action',
  },
  {
    name: 'bg-action-hover',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(10);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-10);
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-action-hover',
  },
  {
    name: 'bg-action-focus',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(10);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-10);
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-action-focus',
  },
  {
    name: 'bg-action-active',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(15);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-15);
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-action-active',
  },
  {
    name: 'bg-sidebar',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['sidebarColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-sidebar',
  },
  {
    name: 'bg-sidebar-item-hover',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['sidebarColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = tinycolor({ a: 0.9, r: 255, b: 255, g: 255 });
      }
      else {
        value = tinycolor({ a: 0.86, r: 15, b: 15, g: 15 });
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-sidebar-item-hover',
  },
  {
    name: 'bg-sidebar-item-selected',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.4);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-sidebar-item-selected',
  },
  {
    name: 'bg-divider',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.6);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-divider',
  },
  {
    name: 'bg-table-selected-row',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.2);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-table-selected-row',
  },
  {
    name: 'bg-toggle-switch-selected-disabled',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.5);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-toggle-switch-selected-disabled',
  },
  {
    name: 'text-link',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-text-link',
  },
  {
    name: 'text-link-hover',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(10);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-10);
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-text-link-hover',
  },
  {
    name: 'text-link-active',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(15);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-15);
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-text-link-active',
  },
  {
    name: 'text-link-focus',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(10);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-10);
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-text-link-focus',
  },
  {
    name: 'text-link-visited',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(20);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-22);
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-text-link-visited',
  },
  {
    name: 'text-sidebar-link',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['sidebarColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = tinycolor({ a: 0.9, b: 255, r: 255, g: 255 });
      }
      else {
        value = tinycolor({ a: 0.86, b: 15, r: 15, g: 15 });
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-text-sidebar-link',
  },
  {
    name: 'text-sidebar-link-hover',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['sidebarColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = tinycolor({ a: 0.86, r: 15, b: 15, g: 15 });
      }
      else {
        value = tinycolor({ a: 0.9, r: 255, b: 255, g: 255 });
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-text-sidebar-link-hover',
  },
  {
    name: 'text-sidebar-link-selected',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['sidebarColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = tinycolor({ a: 0.9, r: 255, b: 255, g: 255 });
      }
      else {
        value = tinycolor({ a: 0.86, b: 15, r: 15, g: 15 });
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-text-sidebar-link-selected',
  },
  {
    name: 'border-color-action',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-border-color-action',
  },
  {
    name: 'border-color-action-hover',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(10);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-10);
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-border-color-action-hover',
  },
  {
    name: 'border-color-action-active',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(15);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-15);
      }
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-border-color-action-active',
  },
  {
    name: 'border-color-input-field',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.6);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-border-color-input-field',
  },
  {
    name: 'border-color-input-field-hover',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(10);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-10);
      }
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.6);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-border-color-input-field-hover',
  },
  {
    name: 'border-color-input-field-focus',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(15);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-15);
      }
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.6);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-border-color-input-field-focus',
  },
  {
    name: 'border-color-input-field-disabled',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = tinycolor({ a: 0.2, r: 15, b: 15, g: 15 });
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-border-color-input-field-disabled',
  },
  {
    name: 'border-color-divider',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.6);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-border-color-divider',
  },
  {
    name: 'border-color-divider-secondary',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(15);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-15);
      }
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.4);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-border-color-divider-secondary',
  },
  {
    name: 'bg-keypad-btn-active',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.3);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-keypad-btn-active',
  },
  {
    name: 'bg-action-secondary-hover',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(10);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-10);
      }
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.15);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-action-secondary-hover',
  },
  {
    name: 'bg-action-secondary-active',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(20);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-20);
      }
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.15);
      return formatValue$2(value);
    },
  },
  {
    name: 'dark-bg-action-secondary-active',
  },
];

const formatValue$1 = (tinyColor) => {
  if (!tinyColor) {
    return '';
  }
  const { r, g, b, a } = tinyColor.toRgb();
  return `${r} ${g} ${b} / ${a}`;
};
const OverridableBorderTokens = [
  {
    name: 'border-secondary-btn',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      return formatValue$1(value);
    },
  },
  {
    name: 'dark-border-secondary-btn',
  },
  {
    name: 'border-secondary-btn-hover',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(10);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-10);
      }
      return formatValue$1(value);
    },
  },
  {
    name: 'dark-border-secondary-btn-hover',
  },
  {
    name: 'border-secondary-btn-active',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(15);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-15);
      }
      return formatValue$1(value);
    },
  },
  {
    name: 'dark-border-secondary-btn-active',
  },
  {
    name: 'border-input-field',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.6);
      return formatValue$1(value);
    },
  },
  {
    name: 'dark-border-input-field',
  },
  {
    name: 'border-input-field-hover',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(10);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-10);
      }
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.6);
      return formatValue$1(value);
    },
  },
  {
    name: 'dark-border-input-field-hover',
  },
  {
    name: 'border-input-field-focus',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(15);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-15);
      }
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.6);
      return formatValue$1(value);
    },
  },
  {
    name: 'dark-border-input-field-focus',
  },
  {
    name: 'border-input-field-disabled',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = tinycolor({ a: 0.2, r: 15, b: 15, g: 15 });
      return formatValue$1(value);
    },
  },
  {
    name: 'dark-border-input-field-disabled',
  },
  {
    name: 'border-divider',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.6);
      return formatValue$1(value);
    },
  },
  {
    name: 'dark-border-divider',
  },
  {
    name: 'border-divider-secondary',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      if (baseColor === null || baseColor === void 0 ? void 0 : baseColor.isDark()) {
        value = value === null || value === void 0 ? void 0 : value.lighten(15);
      }
      else {
        value = value === null || value === void 0 ? void 0 : value.lighten(-15);
      }
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.4);
      return formatValue$1(value);
    },
  },
  {
    name: 'dark-border-divider-secondary',
  },
];

const formatValue = (tinyColor) => {
  if (!tinyColor) {
    return '';
  }
  const { r, g, b, a } = tinyColor.toRgb();
  return `${r} ${g} ${b} / ${a}`;
};
const OverridableRingTokens = [
  {
    name: 'ring-input-focus',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.25);
      return formatValue(value);
    },
  },
  {
    name: 'dark-ring-input-focus',
  },
  {
    name: 'ring-action-focus',
    overrideGetter: (overrideColors, tinycolor) => {
      const overrideBaseColor = overrideColors['actionColor'];
      if (!overrideBaseColor) {
        return '';
      }
      const baseColor = overrideBaseColor
        ? tinycolor(overrideBaseColor)
        : undefined;
      let value = baseColor
        ? baseColor.clone()
        : undefined;
      value = value === null || value === void 0 ? void 0 : value.setAlpha(0.25);
      return formatValue(value);
    },
  },
  {
    name: 'dark-ring-action-focus',
  },
];

/**
 * Vega components theme manager
 */
class ThemeManager {
  /**
   * It takes the colors you passed in, parses them into the correct format, and then applies them to the theme
   *
   * @param {VegaThemeOverrideColors} overrideColors - OverrideColors - This is the object that you pass to the function.
   */
  async overrideColors(overrideColors) {
    const overrideThemes = this.generateOverrideThemes(overrideColors);
    this.applyTheme(overrideThemes);
  }
  /**
   * It removes all the custom override theme styles from the DOM
   */
  async reset() {
    this.getOverrideThemeContainer().innerHTML = '';
  }
  /**
   * This function toggles the dark mode of a webpage by adding or removing a 'dark' class to the HTML element.
   *
   * @param {boolean | undefined} darkMode - A boolean or undefined value that determines whether to enable or disable dark mode.
   * If it is true, dark mode is enabled.
   * If it is false, dark mode is disabled.
   */
  toggleDarkMode(darkMode) {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    }
    else {
      document.documentElement.classList.remove('dark');
    }
  }
  /**
   * The function checks if the HTML element has a class of "dark" and returns a boolean value accordingly.
   *
   * @returns {boolean} A boolean value indicating whether the HTML element has a class of "dark", which is
   * typically used to indicate that the website is in dark mode.
   */
  isDarkMode() {
    return document.documentElement.classList.contains('dark');
  }
  /**
   * It takes a color and generates a bunch of other colors based on that color
   *
   * @param {VegaThemeOverrideColors} overrideColors - OverrideColors - This is the object that is passed in from the user.
   * @returns {OverrideColor[]} Parsed action colors
   */
  generateOverrideThemes(overrideColors) {
    return this.getOverridableTokens().map((item) => {
      return {
        name: item.name,
        value: this.parseCssVarValue(item, overrideColors),
      };
    });
  }
  /**
   * Get the overridable color tokens from design token
   *
   * @returns {OverridableToken[]} overridable action color token array
   */
  getOverridableTokens() {
    return [...OverridableColorTokens, ...OverridableBorderTokens, ...OverridableRingTokens];
  }
  /**
   * Set the custom override theme colors to customOverrideThemeStyleContainer
   *
   * @param {OverrideColor[]} overrideThemes Parsed custom override colors
   */
  applyTheme(overrideThemes) {
    this.getOverrideThemeContainer().innerHTML = `
:root {
\t\t${overrideThemes
      .filter((item) => {
      return !!item.value;
    })
      .map((item) => `--v-${item.name}: ${item.value};`)
      .join('\n\t\t')}
}
				`;
  }
  /**
   * It creates a customOverrideTheme style tag in the head of the document if it doesn't exist, and returns it
   *
   * @returns {Element} The customOverrideThemeStyleContainer is being returned.
   */
  getOverrideThemeContainer() {
    let customOverrideThemeStyleContainer = document.head.querySelector('#vegaCustomOverrideTheme');
    if (!customOverrideThemeStyleContainer) {
      customOverrideThemeStyleContainer = document.createElement('style');
      customOverrideThemeStyleContainer.id = 'vegaCustomOverrideTheme';
      document.head.append(customOverrideThemeStyleContainer);
    }
    return customOverrideThemeStyleContainer;
  }
  /**
   * Calculate and parse color to css var value
   *
   * @param {OverridableToken} overrideColorToken The token will be overridden
   * @param {VegaThemeOverrideColors} overrideColors the payload of override colors
   * @returns {string} The css var value
   */
  parseCssVarValue(overrideColorToken, overrideColors) {
    var _a;
    return (_a = overrideColorToken.overrideGetter) === null || _a === void 0 ? void 0 : _a.call(overrideColorToken, overrideColors, tinycolor);
  }
}
const VegaThemeManager = new ThemeManager();

export { VegaLoader as V, VegaNotify as a, VegaThemeManager as b };
